import type { CheckOverride, CheckResult, OverrideStatus, TotalsCheckResult } from '../../../lib/verification/types'

function StatusBadge({ status }: { status: 'pass' | 'fail' | 'na' }) {
  if (status === 'pass') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-semibold rounded-full">
        Pass
      </span>
    )
  }
  if (status === 'na') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-semibold rounded-full">
        N/A
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-semibold rounded-full">
      Fail
    </span>
  )
}

function DetailRows({ check }: { check: CheckResult }) {
  const rows = check.rows
  if (rows.length === 0) return null
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-gray-500 dark:text-gray-400">
          <tr>
            <th className="text-left py-1 pr-3 font-medium">Item</th>
            <th className="text-left py-1 pr-3 font-medium">Job Value</th>
            <th className="text-left py-1 pr-3 font-medium">CI Value</th>
            <th className="text-left py-1 font-medium">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {rows.map((row, idx) => (
            <tr key={idx}>
              <td className="py-1.5 pr-3 text-gray-900 dark:text-white">{row.label}</td>
              <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300">{row.jobValue ?? '—'}</td>
              <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300">{row.ciValue ?? '—'}</td>
              <td className="py-1.5">
                {row.status === 'match' && (
                  <span className="text-green-600 dark:text-green-400" title={row.note}>Match{row.note ? ` — ${row.note}` : ''}</span>
                )}
                {row.status === 'mismatch' && (
                  <span className="text-red-600 dark:text-red-400" title={row.note}>Mismatch{row.note ? ` — ${row.note}` : ''}</span>
                )}
                {row.status === 'na' && <span className="text-gray-400">N/A</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TotalsBody({ result }: { result: TotalsCheckResult }) {
  return (
    <div className="mt-3 space-y-1.5 text-xs">
      <div className="flex items-center justify-between">
        <span className="text-gray-700 dark:text-gray-300">Packages: Job {result.packages.jobValue} / CI {result.packages.ciValue}</span>
        <StatusBadge status={result.packages.status} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-gray-700 dark:text-gray-300">Gross Weight: Job {result.grossWeight.jobValue.toFixed(3)}kg / CI {result.grossWeight.ciValue.toFixed(3)}kg</span>
        <StatusBadge status={result.grossWeight.status} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-gray-700 dark:text-gray-300">
          {result.cbm.status === 'na'
            ? `CBM: ${result.cbm.note}`
            : `CBM: Job ${result.cbm.jobValue!.toFixed(3)}m³ / CI ${result.cbm.ciValue!.toFixed(3)}m³`}
        </span>
        <StatusBadge status={result.cbm.status} />
      </div>
    </div>
  )
}

function OverrideBadge({ override }: { override: CheckOverride }) {
  const accepted = override.overrideStatus === 'accepted'
  return (
    <div className={`mt-3 p-3 rounded-lg border text-xs ${accepted ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-semibold ${accepted ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400'}`}>
        {accepted ? 'ACCEPTED' : 'REJECTED'}
      </span>
      <span className="ml-2 text-gray-600 dark:text-gray-400">
        by {override.staffEmail} on {new Date(override.createdAt).toLocaleString()}
      </span>
      <p className="mt-1 text-gray-700 dark:text-gray-300">{override.notes}</p>
    </div>
  )
}

interface ResultCardProps {
  check: CheckResult | TotalsCheckResult
  override?: CheckOverride
  onOverride?: (status: OverrideStatus) => void
}

export default function ResultCard({ check, override, onOverride }: ResultCardProps) {
  const isTotals = check.id === 'totals'
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-white">{check.title}</h3>
        <StatusBadge status={check.status} />
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{check.summary}</p>
      {isTotals ? <TotalsBody result={check as TotalsCheckResult} /> : <DetailRows check={check as CheckResult} />}

      {check.status === 'fail' && onOverride && (
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => onOverride('accepted')}
            className="px-3 py-1.5 text-xs font-semibold bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            Accept anyway
          </button>
          <button
            onClick={() => onOverride('rejected')}
            className="px-3 py-1.5 text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Reject
          </button>
        </div>
      )}

      {override && <OverrideBadge override={override} />}
    </div>
  )
}
